"use client";

import Image from "next/image";
import Link from "next/link";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loading } from "react-vant";
import productApi from "@/service/modules/productApi";
import useCartActions from "@/utils/addToCartAction";
import { useAppSelector } from "@/store/hooks";
import { toast } from "@/components/global/Toast";
import type { ProductType } from "@/types/product";
import styles from "@/styles/product-detail/combination.module.scss";

interface Props {
  productId: string | number;
}

type CombinationProduct = ProductType & { productID: number };

const getPromotionRate = (product: CombinationProduct) => {
  if (!product.promotion || Array.isArray(product.promotion)) return 0;
  return Number(product.promotion.promotionRate || 0);
};

const getSaveAmount = (
  products: ProductType[],
  selectedIds: number[],
  totalPrice: number
) => {
  const selectedIdSet = new Set(selectedIds);
  const normalTotal = products.reduce((total, product) => {
    if (!selectedIdSet.has(Number(product.productID))) return total;
    return total + Number(product.normalPrice || product.price || 0);
  }, 0);
  return Math.max(normalTotal - totalPrice, 0);
};

const formatMoney = (value: number) =>
  Number.isFinite(value) ? value.toFixed(2) : "0.00";

export default function Combination({ productId }: Props) {
  const { batchAddToCart } = useCartActions();
  const cartProducts = useAppSelector(
    (state) => state.cartList.data.list.available
  );
  const [products, setProducts] = useState<CombinationProduct[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [saveAmount, setSaveAmount] = useState(0);
  const [calculating, setCalculating] = useState(false);
  const [calculationValid, setCalculationValid] = useState(false);
  const [adding, setAdding] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const calculationSequence = useRef(0);
  const productsRef = useRef<CombinationProduct[]>([]);

  useEffect(() => {
    let active = true;
    calculationSequence.current += 1;
    setLoaded(false);
    setProducts([]);
    productsRef.current = [];
    setSelectedIds([]);
    setTotalPrice(0);
    setSaveAmount(0);
    setCalculating(false);
    setCalculationValid(false);
    setAdding(false);

    const fetchCombination = async () => {
      try {
        const response = await productApi.combine(productId);
        if (!active) return;

        const nextProducts = Array.isArray(response.data?.products)
          ? response.data.products
              .map((item) => ({
                ...item,
                productID: Number(item.productID),
                price: Number(item.price),
                inStock: Number(item.inStock),
              }))
              .filter((item) => item.productID && item.name && item.filename)
          : [];

        productsRef.current = nextProducts;
        setProducts(nextProducts);
        setSelectedIds(
          nextProducts
            .filter((item) => item.inStock > 0)
            .map((item) => item.productID)
        );
      } catch (error) {
        console.error(
          `Failed to fetch combination products, Product: ${productId}`,
          error
        );
      } finally {
        if (active) setLoaded(true);
      }
    };

    fetchCombination();
    return () => {
      active = false;
      calculationSequence.current += 1;
    };
  }, [productId]);

  useEffect(() => {
    if (!loaded || products.length === 0) return;

    const sequence = calculationSequence.current + 1;
    calculationSequence.current = sequence;
    setCalculationValid(false);

    if (selectedIds.length === 0) {
      setTotalPrice(0);
      setSaveAmount(0);
      setCalculating(false);
      setCalculationValid(true);
      return;
    }

    setCalculating(true);
    productApi
      .combineCalculate(selectedIds.map((productID) => ({ productID })))
      .then((response) => {
        if (calculationSequence.current !== sequence) return;

        const summary = response.data?.checkedCombine;
        const nextTotal = Number(summary?.totalPrice);
        if (!summary || !Number.isFinite(nextTotal)) {
          throw new Error("Invalid combination calculation response");
        }

        setTotalPrice(nextTotal);
        setSaveAmount(
          getSaveAmount(
            response.data.products || productsRef.current,
            selectedIds,
            nextTotal
          )
        );
        setCalculationValid(true);

        if (Array.isArray(response.data.products)) {
          const updates = new Map<number, Partial<CombinationProduct>>(
            response.data.products.map((item: Partial<CombinationProduct>) => [
              Number(item.productID),
              item,
            ])
          );
          setProducts((current) => {
            const nextProducts = current.map((item) => {
              const update = updates.get(item.productID);
              return update
                ? {
                    ...item,
                    ...update,
                    productID: Number(update.productID),
                    price: Number(update.price),
                    inStock: Number(update.inStock),
                  }
                : item;
            });
            productsRef.current = nextProducts;
            return nextProducts;
          });
        }
      })
      .catch((error) => {
        if (calculationSequence.current !== sequence) return;
        console.error(
          `Failed to calculate combination products, Product: ${productId}`,
          error
        );
        setTotalPrice(0);
        setSaveAmount(0);
        toast.show("Unable to calculate the combination price. Please try again.");
      })
      .finally(() => {
        if (calculationSequence.current === sequence) setCalculating(false);
      });
  }, [loaded, productId, products.length, selectedIds]);

  const selectableProducts = useMemo(
    () => products.filter((item) => item.inStock > 0),
    [products]
  );

  const allInCart = useMemo(
    () =>
      selectableProducts.length > 0 &&
      selectableProducts.every((product) =>
        cartProducts.some(
          (cartProduct) =>
            cartProduct.productID === product.productID &&
            cartProduct.appSelectStatus === 1
        )
      ),
    [cartProducts, selectableProducts]
  );

  const toggleProduct = useCallback((product: CombinationProduct) => {
    if (product.inStock < 1) return;
    setSelectedIds((current) =>
      current.includes(product.productID)
        ? current.filter((id) => id !== product.productID)
        : [...current, product.productID]
    );
  }, []);

  const handleAction = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (allInCart) {
      window.location.href = "/view-cart";
      return;
    }
    if (!calculationValid || selectedIds.length === 0 || adding) return;

    setAdding(true);
    batchAddToCart(
      event,
      {
        productList: selectedIds.map((pid) => ({ pid, qty: 1, from: 0 })),
      },
      (response: unknown) => {
        setAdding(false);
        if (response !== "error") window.location.href = "/view-cart";
      }
    );
  };

  if (!loaded || products.length === 0) return null;

  const actionDisabled =
    !allInCart &&
    (selectedIds.length === 0 || calculating || !calculationValid || adding);

  return (
    <section className={styles["combination"]}>
      <h2 className={styles["title"]}>Frequently bought together</h2>
      <div className={styles["content"]}>
        <div className={styles["product-area"]}>
          <div
            className={`${styles["product-track"]} ${
              products.length > 3 ? styles["product-track-scrollable"] : ""
            }`}
          >
            {products.map((product) => {
              const selected = selectedIds.includes(product.productID);
              const promotionRate = getPromotionRate(product);
              return (
                <article className={styles["product-item"]} key={product.productID}>
                  <div className={styles["image-wrap"]}>
                    <Link href={product.url || "#"}>
                      <Image
                        className={styles["product-image"]}
                        src={product.filename}
                        alt={product.name}
                        width={96}
                        height={96}
                      />
                    </Link>
                    <button
                      type="button"
                      className={`${styles["checkbox"]} ${
                        selected ? styles["checkbox-selected"] : ""
                      }`}
                      disabled={product.inStock < 1}
                      aria-label={`${selected ? "Remove" : "Add"} ${product.name}`}
                      aria-pressed={selected}
                      onClick={() => toggleProduct(product)}
                    >
                      <span className={styles["checkbox-control"]}>
                        {selected ? "✓" : ""}
                      </span>
                    </button>
                    {product.inStock < 1 && (
                      <span className={styles["sold-out"]}>Sold Out</span>
                    )}
                  </div>
                  {promotionRate > 0 && (
                    <div className={styles["discount"]}>
                      {Math.trunc(promotionRate)} % OFF
                    </div>
                  )}
                  <Link href={product.url || "#"} className={styles["product-name"]}>
                    {product.name}
                  </Link>
                  <div className={styles["product-price"]}>
                    ${formatMoney(product.price)}
                  </div>
                </article>
              );
            })}
          </div>
          {products.length > 3 && (
            <div className={styles["product-fade"]} aria-hidden="true" />
          )}
        </div>

        <aside className={styles["summary"]} aria-live="polite">
          <div className={styles["total-label"]}>Total Price:</div>
          <div className={styles["total-price"]}>${formatMoney(totalPrice)}</div>
          <button
            id="combination-addcart"
            type="button"
            className={styles["add-button"]}
            disabled={actionDisabled}
            onClick={handleAction}
          >
            {adding || calculating ? (
              <Loading color="#fff" size="16px" />
            ) : allInCart ? (
              "Go to cart"
            ) : (
              `Add ${selectedIds.length} to Cart`
            )}
          </button>
          {saveAmount > 0 && (
            <div className={styles["save"]}>save ${formatMoney(saveAmount)}</div>
          )}
        </aside>
      </div>
    </section>
  );
}
